<template>

  <div>
    <Modal
      :title="$t('titles.create_blackout')"
      :submit="submit"
      :submitting="submitting"
    >

      <!-- Organization -->

      <template v-if="$can('organizations.manage')">

        <o-autocomplete
          :label="$t('titles.organization')"
          v-model.number="v$.organizationID.$model"
          :validator="v$.organizationID"
          :items="organizationOptions"
          @update:modelValue="formState.locationIDs = []"
        />

      </template>

      <!-- Location -->

      <o-autocomplete
        :label="$t('titles.locations_one_or_more')"
        v-model="v$.locationIDs.$model"
        :validator="v$.locationIDs"
        :items="locationOptions"
        multiple
        required
      />

      <!-- Name -->

      <o-text-field
        :label="$t('titles.name')"
        v-model="v$.name.$model"
        :validator="v$.name"
        maxlength="50"
        required
      />

      <v-row>

        <v-col cols="12" md="6" class="pb-0 pb-md-4">
          <o-date-time-field
            :date-label="$t('titles.start_date')"
            :time-label="$t('titles.start_time')"
            v-model="v$.startDate.$model"
            :validator="v$.startDate"
            :step="30"
            required
          />
        </v-col>

        <v-col cols="12" md="6" class="pt-0 pt-md-4">
          <o-date-time-field
            :date-label="$t('titles.end_date')"
            :time-label="$t('titles.end_time')"
            v-model="v$.endDate.$model"
            :validator="v$.endDate"
            :step="30"
            required
          />
        </v-col>

      </v-row>

      <!-- Channels -->

      <o-select      
        :label="$t('titles.channels_one_or_more')"
        v-model="v$.channels.$model"
        multiple
        :validator="v$.channels"
        :items="channelOptions"
        :no-data-text="formState.locationIDs.length > 0 ? $t('alerts.location_blackouts_not_possible') : $t('alerts.no_location_selected')"
        :loading="loadingChannelFacilities"
        :disabled="loadingChannelFacilities"
        required
      >

        <!-- https://vuetifyjs.com/en/components/selects/#append-and-prepend-item -->
        <template v-if="channelOptions.length > 0" v-slot:prepend-item>
          <v-list-item title="Select All" @click="toggleAllChannels">
            <template v-slot:prepend>
              <v-checkbox-btn
                :indeterminate="someChannelsSelected && !allChannelsSelected"
                :model-value="someChannelsSelected"
              ></v-checkbox-btn>
            </template>
          </v-list-item>
          <v-divider class="mt-2"></v-divider>
        </template>

      </o-select>

    </Modal>
  </div>

</template>

<script setup lang="ts">

import { computed, inject, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import Modal from '../../Modal.vue'
// @ts-ignore: don't complain about missing typings
import ODateTimeField from '../../forms/ODateTimeField.vue'

// models
import Blackout from '../../../models/Blackout'
import Location from '../../../models/Location'
import LocationProduct from '../../../models/LocationProduct'
import Organization from '../../../models/Organization'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { required, minLength, maxLength } from '../../../utils/i18n-validators'

// misc
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import ChannelRegistry from '../../../channels/ChannelRegistry'
import store from '../../../plugins/vuex'
import { Temporal } from '@js-temporal/polyfill'
import NotificationService from '../../../services/NotificationService'
import _ from 'underscore'
import type { Can } from '../../../main'

const { t } = useI18n()
const $can = inject('$can') as Can

/**
 * Dropdown options
 */

const organizationOptions = computed(() => {
  return Organization.getOptions(true)
})

const locationOptions = computed(() => {

  let options = []

  if (formState.organizationID || formState.organizationID === 0) {
    options = Location.getOptions('blackouts', 'write', formState.organizationID, true)
  }

  return options

})

/**
 * Form state and rules
 */

interface FormState {
  organizationID: number
  locationIDs: number[]
  name: string
  startDate: any
  endDate: any
  channels: string[]
}

const formState = reactive<FormState>({
  organizationID: store.state.auth.user.organizationID,
  locationIDs: [],
  name: '',
  startDate: null,
  endDate: null,
  channels: [],
})

// custom validators

const nowInEarliestFacilityTimeZone = computed<Temporal.PlainDateTime>(() => {

  if (!formState.locationIDs.length) {
    return Temporal.Now.plainDateTimeISO() // just use the browser timezone
  }

  const selectedLocations = Location.query().whereIdIn(formState.locationIDs).get()
  let facilityTimezones = selectedLocations.map(location => location.timezone) // TODO: dedupe (for performance)
  const nows = facilityTimezones.map(timezone => Temporal.Now.zonedDateTimeISO(timezone).toPlainDateTime())
  const sortedNows = nows.toSorted((now1, now2) =>Temporal.PlainDateTime.compare(now1, now2) )
  return sortedNows[0]

})

const isAfterStart = (value: any) => {
  if (!helpers.req(value) || !formState.startDate) {
    return true
  }
  const startDate = Temporal.PlainDateTime.from(formState.startDate)
  const thisDate = Temporal.PlainDateTime.from(value)
  return Temporal.PlainDateTime.compare(startDate, thisDate) === -1
}

const isBeforeEnd = (value: any) => {
  if (!helpers.req(value) || !formState.endDate) {
    return true
  }
  const endDate = Temporal.PlainDateTime.from(formState.endDate)
  const thisDate = Temporal.PlainDateTime.from(value)
  return Temporal.PlainDateTime.compare(endDate, thisDate) === 1
}

const isNotInPast = (value: any) => {

  if (!helpers.req(value)) {
    return true
  }

  const then = Temporal.PlainDateTime.from(value)
  return Temporal.PlainDateTime.compare(nowInEarliestFacilityTimeZone.value, then) === -1

}

const isNoMoreThanOneHourInThePast = (value: any) => {

  if (!helpers.req(value)) {
    return true
  }

  const now = nowInEarliestFacilityTimeZone.value
  const then = Temporal.PlainDateTime.from(value)
  const delta = then.until(now)
  return delta.total('seconds') <= 3600

}

const isOnHalfHour = (value: any) => {
  if (!value) {
    return true
  }
  const date = Temporal.PlainDateTime.from(value)
  return (date.minute === 0 || date.minute === 30) && date.second === 0
}

const isNoLongerThan60DaysAfterStartDate = (value: any) => {
  return (
    !helpers.req(value) ||
    !formState.startDate ||
    Temporal.PlainDateTime.from(formState.startDate).until(Temporal.PlainDateTime.from(value)).days <= 60
  )
}

const formRules = {
  organizationID: {
    required,
  },
  name: {
    required,
    minLength: minLength(3),
    maxLength: maxLength(50),
  },
  locationIDs: {
    required,
  },
  startDate: {
    required,
    isBeforeEnd: helpers.withMessage(
      t('validations.earlier_than_end_date'),
      isBeforeEnd
    ),
    isNoMoreThanOneHourInThePast: helpers.withMessage(
      t('validations.not_more_than_one_hour_in_past'),
      isNoMoreThanOneHourInThePast
    ),
    isOnHalfHour: helpers.withMessage(
      t('validations.on_half_hour'),
      isOnHalfHour
    ),
  },
  endDate: {
    required,
    isAfterStart: helpers.withMessage(
      t('validations.later_than_start_date'),
      isAfterStart
    ),
    isNotInPast: helpers.withMessage(
      t('validations.not_in_past'),
      isNotInPast
    ),
    isOnHalfHour: helpers.withMessage(
      t('validations.on_half_hour'),
      isOnHalfHour
    ),
    isNoLongerThan60DaysAfterStart: helpers.withMessage(
      t('validations.no_more_than_60_days_after_start'),
      isNoLongerThan60DaysAfterStartDate
    ),
  },
  channels: {
    required,
  },
}

const v$ = useVuelidate(formRules, formState)

/**
 * Whenever the Location dropdown is changed, load a list of channel_facilities
 * associated with that location
 */

const loadingChannelFacilities = ref<boolean>(false)

watch(() => formState.locationIDs, () => {
  formState.channels = []
  loadChannels()
})

// keep track of which locations we've already loaded LPs for, so we can
// avoid redundant API calls
let lpsLoadedForLocationIDs: number[] = []

function loadChannels() {

  // if no location is selected, then do nothing
  if (!formState.locationIDs) {
    return
  }

  const locationIDsToLoad = formState.locationIDs.filter((locationID) => !lpsLoadedForLocationIDs.includes(locationID))

  if (!locationIDsToLoad) {
    return
  }

  loadingChannelFacilities.value = true

  const promises = locationIDsToLoad.map((locationID) => {
    // ChannelFacility.fetchByLocationID() returns a list of ChannelFacilities
    // which are associated with the same facility as the Location, but that
    // isn't what we need. We need ChannelFacilities which are bound to the
    // location's LocationProducts.
    return LocationProduct.fetchByLocationID(locationID)
  })

  Promise.all(promises)
    .then(() => {
      lpsLoadedForLocationIDs = _.union(lpsLoadedForLocationIDs, locationIDsToLoad)
    })
    .catch((error) => {
      console.error(error)
      const msg = t('alerts.couldnt_load_channels')
      v$.value.channels.$touch() // make the field dirty, so it will display the error (TODO: this doesn't work)
      NotificationService.error(msg)
    })
    .finally(() => {
      loadingChannelFacilities.value = false
    })

}

/**
 * Channel select
 */

// Returns a list of channels associated with the currently selected location.
const channelOptions = computed(() => {

  if (!formState.locationIDs) {
    return []
  }

  const locationChannels = getChannelsForLocationIDs(formState.locationIDs)
  const enabledChannels = ChannelRegistry.get(['blackouts']).filter((channel) => {
    return locationChannels.includes(channel.name)
  })

  const options = enabledChannels.map((channel) => {
    return {
      value: channel.name,
      title: channel.titleShort
    }
  })

  // enable all channels by default
  formState.channels = enabledChannels.map((channel) => channel.name)

  return options

})

// Returns a list of channels that are associated with the LPs for the specified
// locations
function getChannelsForLocationIDs(locationIDs: number[]) {

  const locationProducts = LocationProduct.query()
    .where('locationID', (locationID: number) => locationIDs.includes(locationID))
    .with('channelFacilities')
    .get()

  const channels: string[] = []

  locationProducts.forEach((locationProduct) => {
    locationProduct.channelFacilities.forEach((channelFacility) => {
      const channel = channelFacility.channel
      if (!channels.includes(channel)) {
        channels.push(channel)
      }
    })
  })

  return channels

}

const someChannelsSelected = computed(() => {
  return formState.channels.length > 0
})

const allChannelsSelected = computed(() => {
  return channelOptions.value.length > 0 && formState.channels.length === channelOptions.value.length
})

function toggleAllChannels() {
  if (allChannelsSelected.value) {
    formState.channels = []
  } else {
    formState.channels = channelOptions.value.map((option) => option.value)
  }
}

/**
 * Submit
 */

const submitting = ref<boolean>(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const promises = formState.locationIDs.map((locationID) => {
    
    const locationChannels = getChannelsForLocationIDs([locationID])
    const channels = formState.channels.filter((channel) => locationChannels.includes(channel))

    return Blackout.saveNew({
      locationID: locationID,
      name: formState.name,
      startDate: formState.startDate,
      endDate: formState.endDate,
      channels: channels,
    })

  })

  Promise.all(promises)
    .then(() => {
      NotificationService.success(t('alerts.blackout_created'))
      closeDialog(null)
    })
    .catch((error) => {
      console.error(error)
      const msg = t('alerts.couldnt_create_blackout')
      NotificationService.error(msg)
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
